import {
  boldExtension,
  createEditorSystem,
  historyExtension,
  italicExtension,
  linkExtension,
  listExtension,
  strikethroughExtension,
  tableExtension,
  underlineExtension,
} from '@lexkit/editor';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Shared LexKit editor system for read-only rich-text rendering. `createEditorSystem` binds a typed
 * `Provider` + `useEditor` to this exact extension set. Every view instance renders its own
 * `<RichTextProvider>`, which gives it an isolated Lexical editor.
 *
 * Ported from the Gold app (cashpile/apps/cashvest) — kept in sync so comments authored in Gold
 * render here with the same formatting. This legacy app only *views* (no editor/toolbar).
 *
 * Scope: bold/italic/underline/strikethrough/lists/links/history.
 */
export const richTextExtensions = [
  boldExtension,
  italicExtension,
  underlineExtension,
  strikethroughExtension,
  listExtension,
  linkExtension,
  historyExtension,
  tableExtension,
] as const;

export const { Provider: RichTextProvider, useEditor: useRichTextEditor } =
  createEditorSystem<typeof richTextExtensions>();

/**
 * Lexical theme: maps each text format to a stable class name. The matching visual styles live in
 * {@link richTextContentSx} (emotion, no CSS file) so formatting renders consistently.
 */
export const richTextTheme = {
  text: {
    bold: 'wysiwyg-bold',
    italic: 'wysiwyg-italic',
    underline: 'wysiwyg-underline',
    strikethrough: 'wysiwyg-strikethrough',
    underlineStrikethrough: 'wysiwyg-underline-strikethrough',
  },
  list: {
    ul: 'wysiwyg-ul',
    ol: 'wysiwyg-ol',
    listitem: 'wysiwyg-li',
  },
  link: 'wysiwyg-link',
  table: 'wysiwyg-table',
  tableRow: 'wysiwyg-tr',
  tableCell: 'wysiwyg-td',
  tableCellHeader: 'wysiwyg-th',
};

export const richTextEditorConfig = { theme: richTextTheme };

/** Styling for Lexical's rendered output; applied to a wrapper Box around the view. */
export const richTextContentSx: SxProps<Theme> = {
  typography: 'body2',
  whiteSpace: 'pre-wrap',
  '& p': { m: 0, '&:not(:last-child)': { mb: 1 } },
  '& .wysiwyg-bold': { fontWeight: 700 },
  '& .wysiwyg-italic': { fontStyle: 'italic' },
  '& .wysiwyg-underline': { textDecoration: 'underline' },
  '& .wysiwyg-strikethrough': { textDecoration: 'line-through' },
  '& .wysiwyg-underline-strikethrough': { textDecoration: 'underline line-through' },
  '& .wysiwyg-ul': { pl: 3, m: 0, listStyleType: 'disc' },
  '& .wysiwyg-ol': { pl: 3, m: 0, listStyleType: 'decimal' },
  '& .wysiwyg-li': { mb: 0.25 },
  '& .wysiwyg-link': { color: 'primary.main', textDecoration: 'underline' },
  '& .wysiwyg-table': {
    borderCollapse: 'collapse',
    my: 1,
    width: 'auto',
    tableLayout: 'fixed',
  },
  '& .wysiwyg-td, & .wysiwyg-th': {
    border: 1,
    borderColor: 'divider',
    p: 0.75,
    verticalAlign: 'top',
    minWidth: 48,
  },
  '& .wysiwyg-th': { bgcolor: 'action.hover', fontWeight: 700 },
};
